// ─────────────────────────────────────────────────────────────────────────────
// Archivo: MxMainTabs.js
// Descripción: Componente dedicado a la gestión visual y de interacción de las pestañas
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { IoClose, IoCloseCircleOutline, IoCopyOutline } from "react-icons/io5";
import { BsPin, BsPinFill } from "react-icons/bs"; 
import { makeStyles } from '@material-ui/core/styles';
import CxTooltip from '../cx/CxTooltip';

// ─── CONSTANTES INTERNAS ─────────────────────────────────────────────────────
const TAB_HEIGHT = 24;

// ─── ESTILOS ─────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  tabBar: {
    position: 'fixed',
    top: 'var(--main-appbar-height)',
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar - 1,
    background: '#e8e8e8',
    borderBottom: '1px solid rgba(0,0,0,0.13)',
    display: 'none',
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 'var(--main-tab-bar-height)',
    padding: '0 4px 0 10px',
    gap: '2px',
    overflowX: 'auto',
    overflowY: 'hidden',
    transition: 'left 0.3s ease',
    '&::-webkit-scrollbar': { height: '3px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.22)', borderRadius: '2px' },
    [theme.breakpoints.up('sm')]: { 
      display: 'flex', 
      left: props => props.boSidebarCollapsed 
        ? 'var(--main-drawer-width-collapsed)' 
        : 'var(--main-drawer-width)' 
    },
  },

  // ─── Pestaña base ─────────────────────────────────────────────────────────
  tab: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '0 8px',
    cursor: 'pointer',
    height: `${TAB_HEIGHT}px`,
    minWidth: 80,
    maxWidth: 190,
    flexShrink: 0,
    background: '#d4d4d4',
    borderRadius: '4px 4px 0 0',
    userSelect: 'none',
    transition: 'background 0.12s',
    boxSizing: 'border-box',
    '&:hover': { background: '#cacaca' },
  },
  tabActive: {
    background: 'var(--ibs_color_white)',
    boxShadow: 'inset 0 2px 0 0 var(--ibs_color_cy_blue)',
    '&:hover': { background: 'var(--ibs_color_white)' },
  },
  tabPinned: {
    boxShadow: 'inset 0 2px 0 0 var(--ibs_color_yellow)',
  },
  tabPinnedActive: {
    background: 'var(--ibs_color_white)',
    boxShadow: 'inset 0 2px 0 0 var(--ibs_color_cy_blue)',
    '&:hover': { background: 'var(--ibs_color_white)' },
  },
  tabDragging: { opacity: 0.45, cursor: 'grabbing' },
  tabDropTarget: { boxShadow: '-3px 0 0 0 var(--ibs_color_cy_blue)' },

  tabPinIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    color: 'var(--ibs_color_yellow)',
  },

  tabLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--ibs_control_label_font_family)',
    fontSize: '11px',
    textAlign: 'left',
  },

  tabCloseBtn: {
    marginLeft: '2px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '13px',
    height: '13px',
    borderRadius: '50%',
    color: 'var(--ibs_color_gray)',
    transition: 'background 0.1s',
    '&:hover': { background: 'rgba(0,0,0,0.18)', color: 'var(--ibs_color_black)' },
  },

  tabActionsGroup: {
    position: 'sticky',
    right: 0,
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    background: '#e8e8e8',
    zIndex: 2,
    borderLeft: '1px solid rgba(0,0,0,0.13)',
  },
  tabActionBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'var(--main-tab-bar-height)',
    height: 'var(--main-tab-bar-height)',
    cursor: 'pointer',
    color: 'var(--ibs_color_gray)',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': { background: '#d8d8d8', color: 'var(--ibs_color_black)' },
  },

  // ─── Menú contextual ──────────────────────────────────────────────────────
  contextMenuBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal,
  },
  contextMenu: {
    position: 'fixed',
    zIndex: theme.zIndex.modal + 1,
    background: 'var(--ibs_color_white)',
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: '6px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    padding: '4px 0',
    minWidth: '175px',
    userSelect: 'none',
  },
  contextMenuHeader: {
    padding: '5px 14px 4px',
    fontSize: '10px',
    fontFamily: 'var(--ibs_control_label_font_family)',
    color: 'var(--ibs_color_gray)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    marginBottom: '3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contextMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '6px 14px',
    fontSize: '12px',
    fontFamily: 'var(--ibs_control_label_font_family)',
    color: 'var(--ibs_color_black)',
    cursor: 'pointer',
    transition: 'background 0.1s',
    '&:hover': { background: 'rgba(0,0,0,0.06)' },
  },
  contextMenuItemPin: {
    color: '#b07d00',
    '&:hover': { background: 'rgba(230,168,23,0.10)' },
  },
}));

// ─── MENÚ CONTEXTUAL ─────────────────────────────────────────────────────────
const TabContextMenu = ({ x, y, tab, onClose, onDuplicate, onTogglePin, classes, trans }) => {
  const menuRef = React.useRef(null);
  const [pos, setPos] = React.useState({ x, y });

  React.useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setPos({
        x: x + rect.width  > window.innerWidth  ? window.innerWidth  - rect.width  - 8 : x,
        y: y + rect.height > window.innerHeight ? window.innerHeight - rect.height - 8 : y,
      });
    }
  }, [x, y]);

  const handle = (fn) => (e) => {
    e.stopPropagation();
    onClose();
    fn();
  };

  return (
    <>
      <div
        className={classes.contextMenuBackdrop}
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />

      {tab.options !== false && (
        <div
          ref={menuRef}
          className={classes.contextMenu}
          style={{ left: pos.x, top: pos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={classes.contextMenuHeader} title={tab.label}>
            {tab.label}
          </div>

          <div className={classes.contextMenuItem} onClick={handle(() => onDuplicate(tab.id))}>
            <IoCopyOutline size={14} />
            {trans('field.tab_duplicate')}
          </div>

          <div
            className={`${classes.contextMenuItem} ${classes.contextMenuItemPin}`}
            onClick={handle(() => onTogglePin(tab.id))}
          >
            {tab.pinned
              ? <><BsPin size={14} /> {trans('field.tab_unpin')}</>
              : <><BsPinFill size={14} /> {trans('field.tab_pin')}</>
            }
          </div>
        </div>
      )}
    </>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const MxMainTabs = ({
  tabs,
  activeTabId,
  onSwitchTab,
  onCloseTab,
  onCloseAllTabs,
  onReorderTabs,
  onDuplicateTab,
  onTogglePinTab,
  showTabs,
  trans,
  boSidebarCollapsed
}) => {
  const classes = useStyles({ boSidebarCollapsed });
  const [dragTabId, setDragTabId] = React.useState(null);
  const [dropTargetId, setDropTargetId] = React.useState(null);
  const [contextMenu, setContextMenu]   = React.useState(null);

  const openContextMenu = (e, tab) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, tab });
  };

  const closeContextMenu = () => setContextMenu(null);

  // ─── Drag & Drop ─────────────────────────────────────────────────────────
  const onTabDragStart = (e, tabId) => {
    setDragTabId(tabId);
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:absolute;top:-9999px';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    e.dataTransfer.effectAllowed = 'move';
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const onTabDragOver = (e, tabId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (tabId !== dragTabId) setDropTargetId(tabId);
  };

  const onTabDragLeave = () => setDropTargetId(null);

  const onTabDrop = (e, targetTabId) => {
    e.preventDefault();
    setDropTargetId(null);
    const sourceId = dragTabId;
    if (!sourceId || sourceId === targetTabId) return;
    if (onReorderTabs) onReorderTabs(sourceId, targetTabId);
  };

  const onTabDragEnd = () => {
    setDragTabId(null);
    setDropTargetId(null);
  };

  if (!showTabs) return null;

  return (
    <div className={classes.tabBar}>
      {tabs.map(tab => {
        const isActive     = tab.id === activeTabId;
        const isPinned     = !!tab.pinned;
        const isDragging   = tab.id === dragTabId;
        const isDropTarget = tab.id === dropTargetId;

        const tabClasses = [classes.tab];
        if (isActive && isPinned) tabClasses.push(classes.tabPinnedActive);
        else if (isActive)        tabClasses.push(classes.tabActive);
        else if (isPinned)        tabClasses.push(classes.tabPinned);
        if (isDragging)           tabClasses.push(classes.tabDragging);
        if (isDropTarget)         tabClasses.push(classes.tabDropTarget);

        const tooltipContent = (
          <span>
            <strong>{tab.label}</strong>
            {isPinned && (
              <><br /><span style={{ color: 'var(--ibs_color_yellow)', fontSize: '10px' }}>📌 {trans('field.tab_pinned')}</span></>
            )}
            <br />
            {tab.options !== false && (
              <span style={{ opacity: 0.7, fontSize: '10px' }}>{trans('field.tab_more_options')}</span>
            )}
          </span>
        );

        return (
          <div
            key={tab.id}
            className={tabClasses.join(' ')}
            draggable
            onClick={() => onSwitchTab(tab.id)}
            onContextMenu={(e) => openContextMenu(e, tab)}
            onDragStart={(e) => onTabDragStart(e, tab.id)}
            onDragOver={(e) => onTabDragOver(e, tab.id)}
            onDragLeave={onTabDragLeave}
            onDrop={(e) => onTabDrop(e, tab.id)}
            onDragEnd={onTabDragEnd}
          >
            {isPinned && (
              <span className={classes.tabPinIcon}>
                <BsPinFill size={10} />
              </span>
            )}

            <CxTooltip text={tooltipContent} arrow>
              <span
                className={classes.tabLabel}
                style={isActive
                  ? { color: 'var(--ibs_color_black)', fontWeight: 600 }
                  : { color: 'var(--ibs_color_gray)' }
                }
              >
                {tab.label}
              </span>
            </CxTooltip>

            {!isPinned && (
              <span
                className={classes.tabCloseBtn}
                onClick={(e) => onCloseTab(e, tab.id)}
              >
                <IoClose size={11} />
              </span>
            )}
          </div>
        );
      })}

      <div className={classes.tabActionsGroup}>
        <CxTooltip text={trans('field.tab_close_all')} arrow>
          <div className={classes.tabActionBtn} onClick={onCloseAllTabs}>
            <IoCloseCircleOutline size={19} />
          </div>
        </CxTooltip>
      </div>

      {contextMenu && (
        <TabContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          tab={contextMenu.tab}
          onClose={closeContextMenu}
          onDuplicate={onDuplicateTab}
          onTogglePin={onTogglePinTab}
          classes={classes}
          trans={trans}
        />
      )}
    </div>
  );
};

export default MxMainTabs;